import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { FormField } from '@/components/ui/form'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { api } from '@/services/api'

export interface FinancialInfoFormData {
  montant_facture_build: number | ''
  montant_run: number | ''
  couts: number | ''
  jours_vendu: number | ''
  jours_reel: number | ''
  paymentStatusId: string
}

interface PaymentStatusOption {
  id: number
  nom: string
}

export interface EditFinancialInfoModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  projectId: string
  initialValues: {
    montant_facture_build?: number | null
    montant_run?: number | null
    couts?: number | null
    jours_vendu?: number | null
    jours_reel?: number | null
    payment_status_id?: number | null
  }
}

export function EditFinancialInfoModal({
  open,
  onOpenChange,
  onSuccess,
  projectId,
  initialValues,
}: EditFinancialInfoModalProps) {
  const [form, setForm] = useState<FinancialInfoFormData>({
    montant_facture_build: '',
    montant_run: '',
    couts: '',
    jours_vendu: '',
    jours_reel: '',
    paymentStatusId: '',
  })
  const [saving, setSaving] = useState(false)
  const [paymentStatuses, setPaymentStatuses] = useState<PaymentStatusOption[]>([])
  const [loadingStatuses, setLoadingStatuses] = useState(false)
  const [showNewStatusInput, setShowNewStatusInput] = useState(false)
  const [newStatusName, setNewStatusName] = useState('')
  const [creatingStatus, setCreatingStatus] = useState(false)

  const fetchPaymentStatuses = async () => {
    setLoadingStatuses(true)
    try {
      const response = await api.get<{ data: PaymentStatusOption[]; count: number }>('/payment-statuses')
      setPaymentStatuses(response.data.data || [])
    } catch (error) {
      console.error('Erreur lors de la récupération des statuts de paiement:', error)
      setPaymentStatuses([])
    } finally {
      setLoadingStatuses(false)
    }
  }

  useEffect(() => {
    if (open) {
      fetchPaymentStatuses()
      setForm({
        montant_facture_build: initialValues.montant_facture_build ?? '',
        montant_run: initialValues.montant_run ?? '',
        couts: initialValues.couts ?? '',
        jours_vendu: initialValues.jours_vendu ?? '',
        jours_reel: initialValues.jours_reel ?? '',
        paymentStatusId: initialValues.payment_status_id != null ? String(initialValues.payment_status_id) : '',
      })
      setShowNewStatusInput(false)
      setNewStatusName('')
    }
  }, [
    open,
    initialValues.montant_facture_build,
    initialValues.montant_run,
    initialValues.couts,
    initialValues.jours_vendu,
    initialValues.jours_reel,
    initialValues.payment_status_id,
  ])

  const handleCreateStatus = async () => {
    if (!newStatusName.trim()) {
      alert('Veuillez entrer un nom pour le statut de paiement')
      return
    }

    setCreatingStatus(true)
    try {
      const response = await api.post<PaymentStatusOption>('/payment-statuses', {
        nom: newStatusName.trim(),
      })
      const newStatus = response.data
      setPaymentStatuses((prev) => [...prev, newStatus])
      setForm((prev) => ({ ...prev, paymentStatusId: String(newStatus.id) }))
      setNewStatusName('')
      setShowNewStatusInput(false)
    } catch (error: unknown) {
      console.error('Erreur lors de la création du statut de paiement:', error)
      const err = error as { message?: string; response?: { detail?: string } }
      alert(err?.response?.detail || err?.message || 'Erreur lors de la création du statut')
    } finally {
      setCreatingStatus(false)
    }
  }

  const toNum = (v: number | ''): number | null => (v === '' ? null : Number(v))

  const handleSubmit = async () => {
    setSaving(true)
    try {
      const payload: Record<string, number | null> = {
        montant_facture_build: toNum(form.montant_facture_build),
        montant_run: toNum(form.montant_run),
        couts: toNum(form.couts),
        jours_vendu: toNum(form.jours_vendu),
        jours_reel: toNum(form.jours_reel),
        payment_status_id: form.paymentStatusId ? Number(form.paymentStatusId) : null,
      }
      await api.put(`/projects/${projectId}`, payload)
      onSuccess()
      onOpenChange(false)
    } catch (error: unknown) {
      console.error('Erreur lors de la mise à jour des informations financières:', error)
      const err = error as { response?: { detail?: string } }
      alert(err?.response?.detail || 'Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Enregistrer
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Informations financières</h2>
          <p className="text-sm text-gray-500 mt-1">Données financières du projet (visible admin uniquement)</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">État du paiement</label>
          {showNewStatusInput ? (
            <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <FormField label="Nom du nouveau statut" required>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newStatusName}
                    onChange={(e) => setNewStatusName(e.target.value)}
                    placeholder="Ex: Payé, En attente, Acompte reçu..."
                    className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleCreateStatus}
                    isLoading={creatingStatus}
                    disabled={!newStatusName.trim() || creatingStatus}
                    className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
                  >
                    Créer
                  </Button>
                </div>
              </FormField>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowNewStatusInput(false)
                  setNewStatusName('')
                }}
                className="text-sm"
              >
                Annuler
              </Button>
            </div>
          ) : (
            <SearchableSelect
              value={form.paymentStatusId}
              onChange={(value) => setForm((prev) => ({ ...prev, paymentStatusId: value }))}
              options={[
                { value: '', label: 'Aucun' },
                ...paymentStatuses.map((status) => ({
                value: String(status.id),
                label: status.nom,
              }))]}
              placeholder={loadingStatuses ? 'Chargement...' : 'Sélectionner un statut de paiement'}
              searchPlaceholder="Rechercher un statut..."
              disabled={loadingStatuses}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
              showAddButton={true}
              addButtonLabel="Ajouter un statut"
              onAddClick={() => setShowNewStatusInput(true)}
            />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant facturé (Build) €</label>
            <Input
              type="number"
              min={0}
              step={1}
              value={form.montant_facture_build}
              onChange={(e) => setForm({ ...form, montant_facture_build: e.target.value === '' ? '' : Number(e.target.value) })}
              placeholder="45000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant de run €/mois</label>
            <Input
              type="number"
              min={0}
              step={1}
              value={form.montant_run}
              onChange={(e) => setForm({ ...form, montant_run: e.target.value === '' ? '' : Number(e.target.value) })}
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coûts €</label>
            <Input
              type="number"
              min={0}
              step={1}
              value={form.couts}
              onChange={(e) => setForm({ ...form, couts: e.target.value === '' ? '' : Number(e.target.value) })}
              placeholder="32400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jours vendu</label>
            <Input
              type="number"
              min={0}
              step={1}
              value={form.jours_vendu}
              onChange={(e) => setForm({ ...form, jours_vendu: e.target.value === '' ? '' : Number(e.target.value) })}
              placeholder="60"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jours réel</label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={form.jours_reel}
              onChange={(e) => setForm({ ...form, jours_reel: e.target.value === '' ? '' : Number(e.target.value) })}
              placeholder="12,5"
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}
